import { useState, useMemo } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Plug2,
  Plus,
  Settings,
  Trash2,
  Check,
  X,
  Github,
  Gitlab,
  Mail,
  MessageSquare,
  Calendar,
  Webhook,
  AlertCircle,
  GitBranch,
  Bug,
  Shield,
  Zap,
  BarChart3,
  Cloud,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  useIntegrations,
  useWebhooks,
  useIntegrationTypes,
  useCreateIntegration,
  useUpdateIntegration,
  useDeleteIntegration,
  useCreateWebhook,
  useUpdateWebhook,
  useDeleteWebhook,
} from '../api/useIntegrations'
import type {
  Integration,
  Webhook as WebhookType,
  IntegrationCreate,
  WebhookCreate,
} from '../api/useIntegrations'

// ---------------------------------------------------------------------------
// Icon / colour helpers
// ---------------------------------------------------------------------------

const getCategoryIcon = (category: string) => {
  const m: Record<string, typeof Plug2> = {
    version_control: GitBranch,
    project_mgmt: Calendar,
    bug_tracking: Bug,
    quality: Shield,
    cicd: Zap,
    testing: BarChart3,
    communication: MessageSquare,
    email: Mail,
    cloud: Cloud,
    monitoring: BarChart3,
  }
  return m[category] || Plug2
}

const getSpecificIcon = (type: string) => {
  const m: Record<string, typeof Plug2> = {
    github: Github,
    gitlab: Gitlab,
    bitbucket: GitBranch,
    slack: MessageSquare,
    teams: MessageSquare,
  }
  return m[type] || null
}

const integrationColor: Record<string, string> = {
  github: 'text-gray-900',
  gitlab: 'text-orange-600',
  bitbucket: 'text-blue-600',
  azure_devops: 'text-blue-500',
  jira: 'text-blue-600',
  asana: 'text-pink-600',
  linear: 'text-purple-600',
  monday: 'text-red-500',
  trello: 'text-blue-500',
  clickup: 'text-purple-500',
  slack: 'text-purple-600',
  teams: 'text-blue-500',
  discord: 'text-indigo-600',
  sendgrid: 'text-blue-600',
  mailchimp: 'text-yellow-600',
  aws: 'text-orange-500',
  azure: 'text-blue-600',
  gcp: 'text-blue-500',
}

const WEBHOOK_EVENTS = [
  { value: 'test_execution_completed', label: 'Test Execution Completed' },
  { value: 'test_execution_failed', label: 'Test Execution Failed' },
  { value: 'test_plan_updated', label: 'Test Plan Updated' },
  { value: 'defect_created', label: 'Defect Created' },
  { value: 'defect_updated', label: 'Defect Updated' },
  { value: 'requirement_created', label: 'Requirement Created' },
]

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function IntegrationsSkeleton() {
  return (
    <div className="space-y-6">
      {[1, 2].map((section) => (
        <Card key={section}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-80 mt-1" />
              </div>
              <Skeleton className="h-10 w-36" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

function IntegrationsErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-16">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-semibold mb-2">
          Failed to Load Integrations
        </h3>
        <p className="text-muted-foreground text-center mb-6 max-w-md">
          Unable to retrieve integration or webhook data. This may be a
          temporary network issue.
        </p>
        <Button variant="outline" onClick={onRetry}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Retry
        </Button>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Type-specific config fields (shared between create & edit)
// ---------------------------------------------------------------------------

interface ConfigFieldsProps {
  type: string
  config: Record<string, any>
  onChange: (config: Record<string, any>) => void
  prefix: string // html-id prefix to avoid collisions
}

function IntegrationConfigFields({
  type,
  config,
  onChange,
  prefix,
}: ConfigFieldsProps) {
  const set = (key: string, val: string) =>
    onChange({ ...config, [key]: val })

  switch (type) {
    case 'github':
    case 'gitlab':
    case 'bitbucket':
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-repo`}>Repository URL *</Label>
            <Input
              id={`${prefix}-repo`}
              placeholder="https://github.com/org/repo"
              value={config.repo || ''}
              onChange={(e) => set('repo', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-token`}>Access Token *</Label>
            <Input
              id={`${prefix}-token`}
              type="password"
              placeholder={
                type === 'github'
                  ? 'ghp_xxxxxxxxxxxx'
                  : type === 'gitlab'
                    ? 'glpat-xxxxxxxxxxxx'
                    : 'token'
              }
              value={config.token || ''}
              onChange={(e) => set('token', e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Personal access token with repo and webhook permissions
            </p>
          </div>
        </>
      )

    case 'jira':
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-url`}>Jira URL *</Label>
            <Input
              id={`${prefix}-url`}
              placeholder="https://yourcompany.atlassian.net"
              value={config.url || ''}
              onChange={(e) => set('url', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-email`}>Email *</Label>
            <Input
              id={`${prefix}-email`}
              type="email"
              placeholder="your-email@company.com"
              value={config.email || ''}
              onChange={(e) => set('email', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-token`}>API Token *</Label>
            <Input
              id={`${prefix}-token`}
              type="password"
              placeholder="Atlassian API token"
              value={config.token || ''}
              onChange={(e) => set('token', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-project`}>Project Key</Label>
            <Input
              id={`${prefix}-project`}
              placeholder="PROJ"
              value={config.projectKey || ''}
              onChange={(e) => set('projectKey', e.target.value)}
            />
          </div>
        </>
      )

    case 'asana':
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-token`}>Personal Access Token *</Label>
            <Input
              id={`${prefix}-token`}
              type="password"
              placeholder="1/1234567890:abcdef..."
              value={config.token || ''}
              onChange={(e) => set('token', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-workspace`}>Workspace GID</Label>
            <Input
              id={`${prefix}-workspace`}
              placeholder="1234567890123456"
              value={config.workspaceId || ''}
              onChange={(e) => set('workspaceId', e.target.value)}
            />
          </div>
        </>
      )

    case 'slack':
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-webhook`}>Webhook URL *</Label>
            <Input
              id={`${prefix}-webhook`}
              placeholder="https://hooks.slack.com/services/..."
              value={config.webhookUrl || ''}
              onChange={(e) => set('webhookUrl', e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Create at{' '}
              <a
                href="https://api.slack.com/messaging/webhooks"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Slack Incoming Webhooks
              </a>
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-channel`}>Default Channel</Label>
            <Input
              id={`${prefix}-channel`}
              placeholder="#qa-team"
              value={config.channel || ''}
              onChange={(e) => set('channel', e.target.value)}
            />
          </div>
        </>
      )

    case 'teams':
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-webhook`}>Incoming Webhook URL *</Label>
            <Input
              id={`${prefix}-webhook`}
              placeholder="https://outlook.office.com/webhook/..."
              value={config.webhookUrl || ''}
              onChange={(e) => set('webhookUrl', e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Create via Microsoft Teams connector configuration
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-channel`}>Channel Name</Label>
            <Input
              id={`${prefix}-channel`}
              placeholder="General"
              value={config.channel || ''}
              onChange={(e) => set('channel', e.target.value)}
            />
          </div>
        </>
      )

    default:
      // Generic fallback for types loaded dynamically from /api/integrations/types
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-url`}>Service URL</Label>
            <Input
              id={`${prefix}-url`}
              placeholder="https://..."
              value={config.url || ''}
              onChange={(e) => set('url', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-token`}>Access Token / API Key</Label>
            <Input
              id={`${prefix}-token`}
              type="password"
              placeholder="Token or API key"
              value={config.token || ''}
              onChange={(e) => set('token', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-project`}>Project / Workspace ID</Label>
            <Input
              id={`${prefix}-project`}
              placeholder="project-id or workspace-id"
              value={config.projectId || config.workspaceId || ''}
              onChange={(e) =>
                onChange({
                  ...config,
                  projectId: e.target.value,
                  workspaceId: e.target.value,
                })
              }
            />
          </div>
        </>
      )
  }
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function IntegrationsPage() {
  // -- API data ---------------------------------------------------------------

  const {
    data: integrations = [],
    isLoading: loadingIntegrations,
    isError: integrationsError,
    refetch: refetchIntegrations,
  } = useIntegrations()

  const {
    data: webhooks = [],
    isLoading: loadingWebhooks,
    isError: webhooksError,
    refetch: refetchWebhooks,
  } = useWebhooks()

  const { data: integrationTypesData, isLoading: loadingTypes } =
    useIntegrationTypes()

  const createIntegrationMut = useCreateIntegration()
  const updateIntegrationMut = useUpdateIntegration()
  const deleteIntegrationMut = useDeleteIntegration()
  const createWebhookMut = useCreateWebhook()
  const updateWebhookMut = useUpdateWebhook()
  const deleteWebhookMut = useDeleteWebhook()

  // -- Dialog state -----------------------------------------------------------

  const [integrationDialogOpen, setIntegrationDialogOpen] = useState(false)
  const [configDialogOpen, setConfigDialogOpen] = useState(false)
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false)
  const [editIntegrationDialogOpen, setEditIntegrationDialogOpen] =
    useState(false)
  const [editWebhookDialogOpen, setEditWebhookDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // -- Selection / form state -------------------------------------------------

  const [selectedType, setSelectedType] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')

  const [newIntegration, setNewIntegration] = useState({
    name: '',
    description: '',
    config: {} as Record<string, any>,
  })

  const [newWebhook, setNewWebhook] = useState({
    name: '',
    url: '',
    description: '',
    events: [] as string[],
    secret: '',
  })

  const [editingIntegration, setEditingIntegration] =
    useState<Integration | null>(null)
  const [editingWebhook, setEditingWebhook] = useState<WebhookType | null>(
    null
  )
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'integration' | 'webhook'
    id: number
    name: string
  } | null>(null)

  // -- Derived ----------------------------------------------------------------

  const integrationTypes = useMemo(() => {
    if (!integrationTypesData) return []
    return Object.entries(integrationTypesData.integration_types).map(
      ([key, value]) => {
        const specific = getSpecificIcon(key)
        const catIcon = getCategoryIcon(value.category)
        return {
          value: key,
          label: value.name,
          description: value.description,
          icon: specific || catIcon,
          color: integrationColor[key] || 'text-gray-600',
          category: value.category,
        }
      }
    )
  }, [integrationTypesData])

  const categoryLabels = useMemo(() => {
    if (!integrationTypesData) return {} as Record<string, string>
    return integrationTypesData.categories
  }, [integrationTypesData])

  const filteredTypes = useMemo(() => {
    let list = integrationTypes
    if (selectedCategory !== 'all') {
      list = list.filter((t) => t.category === selectedCategory)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      list = list.filter(
        (t) =>
          t.label.toLowerCase().includes(q) ||
          t.value.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q)
      )
    }
    return list
  }, [integrationTypes, selectedCategory, searchQuery])

  // -- Handlers: Integrations -------------------------------------------------

  const handlePickType = () => {
    if (!selectedType) {
      toast.error('Please select an integration type')
      return
    }
    setIntegrationDialogOpen(false)
    setConfigDialogOpen(true)
  }

  const handleSaveNewIntegration = async () => {
    if (!newIntegration.name.trim()) {
      toast.error('Please provide a name for the integration')
      return
    }
    try {
      await createIntegrationMut.mutateAsync({
        integration_type: selectedType,
        name: newIntegration.name.trim(),
        description: newIntegration.description.trim() || undefined,
        config: newIntegration.config,
        is_active: true,
      } as IntegrationCreate)
      toast.success('Integration added successfully')
      setConfigDialogOpen(false)
      setSelectedType('')
      setNewIntegration({ name: '', description: '', config: {} })
    } catch {
      toast.error('Failed to add integration')
    }
  }

  const handleEditIntegration = (i: Integration) => {
    setEditingIntegration({ ...i })
    setEditIntegrationDialogOpen(true)
  }

  const handleSaveIntegration = async () => {
    if (!editingIntegration) return
    try {
      await updateIntegrationMut.mutateAsync({
        id: editingIntegration.id,
        data: {
          name: editingIntegration.name,
          description: editingIntegration.description,
          config: editingIntegration.config,
          is_active: editingIntegration.is_active,
        },
      })
      toast.success('Integration updated successfully')
      setEditIntegrationDialogOpen(false)
      setEditingIntegration(null)
    } catch {
      toast.error('Failed to update integration')
    }
  }

  const handleToggleIntegration = async (i: Integration) => {
    try {
      await updateIntegrationMut.mutateAsync({
        id: i.id,
        data: { is_active: !i.is_active },
      })
      toast.success(
        `${i.name} ${i.is_active ? 'disabled' : 'enabled'} successfully`
      )
    } catch {
      toast.error('Failed to update integration status')
    }
  }

  // -- Handlers: Webhooks -----------------------------------------------------

  const handleAddWebhook = async () => {
    if (
      !newWebhook.name.trim() ||
      !newWebhook.url.trim() ||
      newWebhook.events.length === 0
    ) {
      toast.error('Please fill in all required fields')
      return
    }
    try {
      await createWebhookMut.mutateAsync({
        name: newWebhook.name.trim(),
        url: newWebhook.url.trim(),
        description: newWebhook.description.trim() || undefined,
        events: newWebhook.events,
        secret: newWebhook.secret || undefined,
        is_active: true,
      } as WebhookCreate)
      toast.success('Webhook added successfully')
      setWebhookDialogOpen(false)
      setNewWebhook({
        name: '',
        url: '',
        description: '',
        events: [],
        secret: '',
      })
    } catch {
      toast.error('Failed to add webhook')
    }
  }

  const handleEditWebhook = (w: WebhookType) => {
    setEditingWebhook({ ...w })
    setEditWebhookDialogOpen(true)
  }

  const handleSaveWebhook = async () => {
    if (!editingWebhook) return
    try {
      await updateWebhookMut.mutateAsync({
        id: editingWebhook.id,
        data: {
          name: editingWebhook.name,
          url: editingWebhook.url,
          description: editingWebhook.description,
          events: editingWebhook.events,
          is_active: editingWebhook.is_active,
        },
      })
      toast.success('Webhook updated successfully')
      setEditWebhookDialogOpen(false)
      setEditingWebhook(null)
    } catch {
      toast.error('Failed to update webhook')
    }
  }

  const handleToggleWebhook = async (w: WebhookType) => {
    try {
      await updateWebhookMut.mutateAsync({
        id: w.id,
        data: { is_active: !w.is_active },
      })
      toast.success(
        `${w.name} ${w.is_active ? 'disabled' : 'enabled'} successfully`
      )
    } catch {
      toast.error('Failed to update webhook status')
    }
  }

  // -- Handlers: Delete -------------------------------------------------------

  const openDelete = (
    type: 'integration' | 'webhook',
    id: number,
    name: string
  ) => {
    setDeleteTarget({ type, id, name })
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      if (deleteTarget.type === 'integration') {
        await deleteIntegrationMut.mutateAsync(deleteTarget.id)
      } else {
        await deleteWebhookMut.mutateAsync(deleteTarget.id)
      }
      toast.success(
        `${deleteTarget.type === 'integration' ? 'Integration' : 'Webhook'} deleted`
      )
      setDeleteDialogOpen(false)
      setDeleteTarget(null)
    } catch {
      toast.error(`Failed to delete ${deleteTarget.type}`)
    }
  }

  // -- Webhook event toggle helpers -------------------------------------------

  const toggleNewWebhookEvent = (event: string) =>
    setNewWebhook((p) => ({
      ...p,
      events: p.events.includes(event)
        ? p.events.filter((e) => e !== event)
        : [...p.events, event],
    }))

  const toggleEditWebhookEvent = (event: string) => {
    if (!editingWebhook) return
    setEditingWebhook((p) =>
      p
        ? {
            ...p,
            events: p.events.includes(event)
              ? p.events.filter((e) => e !== event)
              : [...p.events, event],
          }
        : p
    )
  }

  // -- Icon helper for existing integration cards -----------------------------

  const iconForType = (type: string) => {
    const t = integrationTypes.find((x) => x.value === type)
    return t?.icon || Plug2
  }

  const colorForType = (type: string) =>
    integrationColor[type] || 'text-gray-600'

  // -- Render -----------------------------------------------------------------

  const isLoading = loadingIntegrations || loadingWebhooks || loadingTypes
  if (isLoading) return <IntegrationsSkeleton />
  if (integrationsError || webhooksError)
    return (
      <IntegrationsErrorState
        onRetry={() => {
          refetchIntegrations()
          refetchWebhooks()
        }}
      />
    )

  return (
    <div className="space-y-6">
      {/* ================================================================== */}
      {/* Third-Party Integrations                                           */}
      {/* ================================================================== */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Plug2 className="h-5 w-5" />
                Third-Party Integrations
              </CardTitle>
              <CardDescription>
                Connect external services like GitHub, Jira, Asana, and more
              </CardDescription>
            </div>
            <Button onClick={() => setIntegrationDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Integration
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {integrations.length > 0 ? (
              integrations.map((integration) => {
                const Icon = iconForType(integration.integration_type)
                return (
                  <Card key={integration.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div
                            className={`p-2 rounded-lg bg-muted ${colorForType(integration.integration_type)}`}
                          >
                            <Icon className="h-6 w-6" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">
                                {integration.name}
                              </h3>
                              <Badge variant="outline">
                                {integration.integration_type}
                              </Badge>
                              <Badge
                                variant={
                                  integration.is_active
                                    ? 'default'
                                    : 'secondary'
                                }
                              >
                                {integration.is_active ? (
                                  <Check className="h-3 w-3 mr-1" />
                                ) : (
                                  <X className="h-3 w-3 mr-1" />
                                )}
                                {integration.is_active
                                  ? 'Active'
                                  : 'Disabled'}
                              </Badge>
                            </div>
                            {integration.description && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {integration.description}
                              </p>
                            )}
                            {integration.last_sync && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Last synced:{' '}
                                {new Date(
                                  integration.last_sync
                                ).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={integration.is_active}
                            onCheckedChange={() =>
                              handleToggleIntegration(integration)
                            }
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              handleEditIntegration(integration)
                            }
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              openDelete(
                                'integration',
                                integration.id,
                                integration.name
                              )
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No integrations configured yet. Click "Add Integration" to get
                started.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ================================================================== */}
      {/* Webhooks                                                           */}
      {/* ================================================================== */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Webhook className="h-5 w-5" />
                Webhooks
              </CardTitle>
              <CardDescription>
                Configure webhooks to receive real-time notifications about
                events
              </CardDescription>
            </div>
            <Button onClick={() => setWebhookDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Webhook
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {webhooks.length > 0 ? (
              webhooks.map((webhook) => (
                <Card key={webhook.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 rounded-lg bg-muted text-blue-600">
                          <Webhook className="h-6 w-6" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{webhook.name}</h3>
                            <Badge
                              variant={
                                webhook.is_active ? 'default' : 'secondary'
                              }
                            >
                              {webhook.is_active ? (
                                <Check className="h-3 w-3 mr-1" />
                              ) : (
                                <X className="h-3 w-3 mr-1" />
                              )}
                              {webhook.is_active ? 'Active' : 'Disabled'}
                            </Badge>
                            {webhook.failure_count > 0 && (
                              <Badge variant="destructive">
                                {webhook.failure_count} failures
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 font-mono truncate max-w-md">
                            {webhook.url}
                          </p>
                          <div className="flex gap-1 mt-2 flex-wrap">
                            {webhook.events.map((ev) => (
                              <Badge
                                key={ev}
                                variant="outline"
                                className="text-xs"
                              >
                                {ev.replace(/_/g, ' ')}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={webhook.is_active}
                          onCheckedChange={() => handleToggleWebhook(webhook)}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditWebhook(webhook)}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            openDelete('webhook', webhook.id, webhook.name)
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No webhooks configured yet. Click "Add Webhook" to create one.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ================================================================== */}
      {/* Dialog: Pick Integration Type                                      */}
      {/* ================================================================== */}
      <Dialog
        open={integrationDialogOpen}
        onOpenChange={setIntegrationDialogOpen}
      >
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Add Integration</DialogTitle>
            <DialogDescription>
              Choose from {integrationTypes.length}+ third-party services to
              integrate with your organization
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search integrations…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                onClick={() => setSelectedCategory('all')}
              >
                All
              </Button>
              {Object.entries(categoryLabels).map(([key, label]) => (
                <Button
                  key={key}
                  size="sm"
                  variant={
                    selectedCategory === key ? 'default' : 'outline'
                  }
                  onClick={() => setSelectedCategory(key)}
                >
                  {label}
                </Button>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[50vh] overflow-y-auto">
              {filteredTypes.map((t) => {
                const Icon = t.icon
                return (
                  <button
                    key={t.value}
                    type="button"
                    className={`flex items-center gap-3 p-3 border rounded-lg text-left transition-colors hover:bg-accent ${
                      selectedType === t.value
                        ? 'border-primary bg-accent'
                        : 'border-border'
                    }`}
                    onClick={() => setSelectedType(t.value)}
                  >
                    <div className={`p-1.5 rounded ${t.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">
                        {t.label}
                      </p>
                      {t.description && (
                        <p className="text-xs text-muted-foreground truncate">
                          {t.description}
                        </p>
                      )}
                    </div>
                  </button>
                )
              })}
              {filteredTypes.length === 0 && (
                <div className="col-span-full text-center py-8 text-muted-foreground">
                  No integrations match your search
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIntegrationDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handlePickType} disabled={!selectedType}>
              Configure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* Dialog: Configure New Integration                                  */}
      {/* ================================================================== */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Configure Integration</DialogTitle>
            <DialogDescription>
              Set up your{' '}
              {integrationTypes.find((t) => t.value === selectedType)?.label ||
                selectedType}{' '}
              integration
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-int-name">Name *</Label>
              <Input
                id="new-int-name"
                placeholder="e.g., Production GitHub"
                value={newIntegration.name}
                onChange={(e) =>
                  setNewIntegration({
                    ...newIntegration,
                    name: e.target.value,
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-int-desc">Description</Label>
              <Textarea
                id="new-int-desc"
                placeholder="Optional description"
                value={newIntegration.description}
                onChange={(e) =>
                  setNewIntegration({
                    ...newIntegration,
                    description: e.target.value,
                  })
                }
                rows={2}
              />
            </div>
            <IntegrationConfigFields
              type={selectedType}
              config={newIntegration.config}
              onChange={(c) =>
                setNewIntegration({ ...newIntegration, config: c })
              }
              prefix="new"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfigDialogOpen(false)
                setIntegrationDialogOpen(true)
              }}
            >
              Back
            </Button>
            <Button
              onClick={handleSaveNewIntegration}
              disabled={createIntegrationMut.isPending}
            >
              {createIntegrationMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Add Integration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* Dialog: Edit Integration                                           */}
      {/* ================================================================== */}
      <Dialog
        open={editIntegrationDialogOpen}
        onOpenChange={setEditIntegrationDialogOpen}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Integration</DialogTitle>
            <DialogDescription>
              Update configuration for {editingIntegration?.name}
            </DialogDescription>
          </DialogHeader>
          {editingIntegration && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-int-name">Name *</Label>
                <Input
                  id="edit-int-name"
                  value={editingIntegration.name}
                  onChange={(e) =>
                    setEditingIntegration({
                      ...editingIntegration,
                      name: e.target.value,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-int-desc">Description</Label>
                <Textarea
                  id="edit-int-desc"
                  value={editingIntegration.description || ''}
                  onChange={(e) =>
                    setEditingIntegration({
                      ...editingIntegration,
                      description: e.target.value,
                    })
                  }
                  rows={2}
                />
              </div>
              <IntegrationConfigFields
                type={editingIntegration.integration_type}
                config={editingIntegration.config}
                onChange={(c) =>
                  setEditingIntegration({
                    ...editingIntegration,
                    config: c,
                  })
                }
                prefix="edit"
              />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditIntegrationDialogOpen(false)}
              disabled={updateIntegrationMut.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveIntegration}
              disabled={updateIntegrationMut.isPending}
            >
              {updateIntegrationMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* Dialog: Add Webhook                                                */}
      {/* ================================================================== */}
      <Dialog open={webhookDialogOpen} onOpenChange={setWebhookDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Webhook</DialogTitle>
            <DialogDescription>
              Configure a webhook to receive notifications about events
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="wh-name">Name *</Label>
              <Input
                id="wh-name"
                placeholder="e.g., Slack Notifications"
                value={newWebhook.name}
                onChange={(e) =>
                  setNewWebhook({ ...newWebhook, name: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wh-url">Webhook URL *</Label>
              <Input
                id="wh-url"
                type="url"
                placeholder="https://hooks.example.com/..."
                value={newWebhook.url}
                onChange={(e) =>
                  setNewWebhook({ ...newWebhook, url: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wh-desc">Description</Label>
              <Textarea
                id="wh-desc"
                placeholder="Optional description"
                value={newWebhook.description}
                onChange={(e) =>
                  setNewWebhook({
                    ...newWebhook,
                    description: e.target.value,
                  })
                }
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Events to Trigger *</Label>
              <div className="grid grid-cols-2 gap-2">
                {WEBHOOK_EVENTS.map((ev) => (
                  <div
                    key={ev.value}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={`new-ev-${ev.value}`}
                      checked={newWebhook.events.includes(ev.value)}
                      onCheckedChange={() => toggleNewWebhookEvent(ev.value)}
                    />
                    <Label
                      htmlFor={`new-ev-${ev.value}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {ev.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="wh-secret">Secret (Optional)</Label>
              <Input
                id="wh-secret"
                type="password"
                placeholder="For HMAC signature verification"
                value={newWebhook.secret}
                onChange={(e) =>
                  setNewWebhook({ ...newWebhook, secret: e.target.value })
                }
              />
              <p className="text-xs text-muted-foreground">
                Used to verify webhook payload authenticity
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setWebhookDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddWebhook}
              disabled={createWebhookMut.isPending}
            >
              {createWebhookMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Add Webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* Dialog: Edit Webhook                                               */}
      {/* ================================================================== */}
      <Dialog
        open={editWebhookDialogOpen}
        onOpenChange={setEditWebhookDialogOpen}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Webhook</DialogTitle>
            <DialogDescription>
              Update configuration for {editingWebhook?.name}
            </DialogDescription>
          </DialogHeader>
          {editingWebhook && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-wh-name">Name *</Label>
                <Input
                  id="edit-wh-name"
                  value={editingWebhook.name}
                  onChange={(e) =>
                    setEditingWebhook({
                      ...editingWebhook,
                      name: e.target.value,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-wh-url">Webhook URL *</Label>
                <Input
                  id="edit-wh-url"
                  type="url"
                  value={editingWebhook.url}
                  onChange={(e) =>
                    setEditingWebhook({
                      ...editingWebhook,
                      url: e.target.value,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-wh-desc">Description</Label>
                <Textarea
                  id="edit-wh-desc"
                  value={editingWebhook.description || ''}
                  onChange={(e) =>
                    setEditingWebhook({
                      ...editingWebhook,
                      description: e.target.value,
                    })
                  }
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Events to Trigger *</Label>
                <div className="grid grid-cols-2 gap-2">
                  {WEBHOOK_EVENTS.map((ev) => (
                    <div
                      key={ev.value}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={`edit-ev-${ev.value}`}
                        checked={editingWebhook.events.includes(ev.value)}
                        onCheckedChange={() =>
                          toggleEditWebhookEvent(ev.value)
                        }
                      />
                      <Label
                        htmlFor={`edit-ev-${ev.value}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {ev.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditWebhookDialogOpen(false)}
              disabled={updateWebhookMut.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveWebhook}
              disabled={updateWebhookMut.isPending}
            >
              {updateWebhookMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* Dialog: Delete Confirmation                                        */}
      {/* ================================================================== */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Delete{' '}
              {deleteTarget?.type === 'integration'
                ? 'Integration'
                : 'Webhook'}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-medium text-foreground">
                {deleteTarget?.name}
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={
                deleteIntegrationMut.isPending || deleteWebhookMut.isPending
              }
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={
                deleteIntegrationMut.isPending || deleteWebhookMut.isPending
              }
            >
              {(deleteIntegrationMut.isPending ||
                deleteWebhookMut.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
